<template>
  <div class="RightSideBar">
    <div
        id="RightSideBarContainer"
        class="RightSideBarContainer"
        ref="sidebarEl"
        tabindex="-1"
        aria-label="Sidebar Navigation"
    >
      <!-- Wheel: Goal/Target -->
      <div v-if="viewMode === 'wheel' && (rightSidebarView === 'goal' || rightSidebarView === 'target')">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="details" href="#details">{{ goalOrTarget }} Details</MDBTabItem>
            <MDBTabItem tabId="selections" href="#selections">Target Selections</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="details" id="details">
              <GoalDetails />
            </MDBTabPane>
            <MDBTabPane tabId="selections" id="selections">
              <AllGoals />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- AnalysisName (wheel) -->
      <div v-if="rightSidebarView === 'AnalysisName' && viewMode === 'wheel'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Analysis Name</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <AnalysisName />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- AnalysisName (matrix) -->
      <div v-if="rightSidebarView === 'AnalysisName' && viewMode === 'matrix'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Analysis Name</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <CrosswalkAnalysisName />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Strategy Details (matrix) -->
      <div v-if="viewMode === 'matrix' && rightSidebarView === 'StrategyDetails'">
        <div class="crosswalk-instructions">
          <span class="crosswalk-instructions-step">Step 4: </span>
          Consider the SDG item on the vertical axis in relation to the priority on the horizontal axis. In the box indicated, indicate an assessment of how you are doing.
          <strong>Yes:</strong> if SDG aligned activities are underway.
          <strong>Developing:</strong> if planned or under development
          <strong>No:</strong> if there is no attention to this item
          <strong>For discussion:</strong> if more information is needed or a discussion is needed. It is important to focus on strengths and assets during this kind of preliminary assessment. Add notes to reflect discussion questions or follow-up items.
        </div>
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Details</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <StrategyDetails />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Downloads (matrix) -->
      <div v-if="viewMode === 'matrix' && rightSidebarView === 'Downloads'">
        <div class="crosswalk-instructions">
          <span class="crosswalk-instructions-step">Step 5: </span>
          Use <strong>Download Image</strong> button to create a Visual Graphic. Visual graphics can be produced with or without a legend. In order to save and revise your graphic over time, you must be a registered user.
          <strong>Save</strong> your work using the save button above.
          <strong>Notes</strong> can be downloaded along with all analysis details by clicking the Download Data (CSV) button.
        </div>
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Downloads</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <DownloadData
                  @print="$emit('print')"
                  @print-matrix-only="$emit('print-matrix-only')"
                  @download-csv="$emit('download-csv')"
              />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Publish (matrix) -->
      <div v-if="viewMode === 'matrix' && rightSidebarView === 'Publish'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Publish</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <PublishData analysisType="crosswalk" />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Publish (wheel) -->
      <div v-if="viewMode === 'wheel' && rightSidebarView === 'Publish'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Publish</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <PublishData analysisType="wheel" />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- SingleStrategySetup (matrix) -->
      <div v-if="viewMode === 'matrix' && (rightSidebarView === 'SingleStrategySetup' || strategySetupMode === true) && rightSidebarView !== 'Legend'">
        <div class="crosswalk-instructions">
          <span class="crosswalk-instructions-step">Step 3: </span>
          In order to define the horizontal axis, select keywords from the Strategic Priorities you identified earlier. Enter up to seven headings on the horizontal axis and take care to include the language of the full strategy just below.
        </div>
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Strategy Setup</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <SingleStrategySetup />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Legend (matrix) -->
      <div v-if="viewMode === 'matrix' && rightSidebarView === 'Legend'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Legend</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <CrosswalkLegend />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Legend (wheel) -->
      <div v-if="viewMode === 'wheel' && rightSidebarView === 'Legend'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Legend</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <WheelLegend />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- ChallengeType (matrix) -->
      <div v-if="viewMode === 'matrix' && rightSidebarView === 'ChallengeType'">
        <div class="crosswalk-instructions">
          <span class="crosswalk-instructions-step">Step 1: </span>
          Select Challenge and Type of Plan. This analysis depends on identifying one or more action plans related to your focus topic. It is recommended to add a link if available in your notes for easy reference. Please indicate which types of plan(s) you are using.
          <br /><br />
          Review the Plan with key stakeholders and Identify Core Themes for consideration. Generally, this will align with strategic priorities and major headings from your plan. (It can be helpful to refer to the outline or table of contents). You will use this information in step 3!
        </div>
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Plan Type</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <ChallengeView />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- ImageView -->
      <div v-if="rightSidebarView === 'ImageView'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Image</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <ImageView />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- AboutOrg -->
      <div v-if="rightSidebarView === 'AboutOrg'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Organization</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <AboutOrg />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- KeywordView -->
      <div v-if="rightSidebarView === 'KeywordView'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Keywords</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <KeywordView />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- ChallengeType (wheel) -->
      <div v-if="viewMode !== 'matrix' && rightSidebarView === 'ChallengeType'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Challenge Type</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <ChallengeType />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Review Visualization -->
      <div v-if="rightSidebarView === 'ReviewWheel'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Review Visualization</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <ReviewGoals />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Challenge Statement -->
      <div v-if="rightSidebarView === 'ChallengeStatement'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Challenge Statement</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <ChallengeStatement />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- MatrixType -->
      <div v-if="viewMode === 'matrix' && rightSidebarView === 'MatrixType'">
        <div class="crosswalk-instructions">
          <span class="crosswalk-instructions-step">Step 2: </span>
          Determine your preferred level of analysis by considering if you would like to begin with a holistic crosswalk analysis of all 17 SDGs OR choose one key SDG (e.g. SDG 6 Water or SDG 11 Communities) and consider all or selected sub-targets. Your selection will automatically appear on the vertical axis of the matrix.
        </div>
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">Analysis Options</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <MatrixType />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- About Me -->
      <div v-if="rightSidebarView === 'AboutMe'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">About Me</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <AboutMe />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Primary Goals -->
      <div v-if="rightSidebarView === 'PrimaryGoals'">
        <MDBTabs v-model="activeTabId1">
          <MDBTabNav fill tabsClasses="mb-2">
            <MDBTabItem tabId="selections" href="#selections">{{ primaryGoalTitle }}</MDBTabItem>
          </MDBTabNav>
          <MDBTabContent>
            <MDBTabPane tabId="selections" id="selections">
              <PrimaryGoals />
            </MDBTabPane>
          </MDBTabContent>
        </MDBTabs>
      </div>

      <!-- Prev/Next/Submit -->
      <div class="prev-next">
        <a v-if="showPrevious" href="#" @click="clickPrevious" class="btn btn-outline-primary">Previous</a>
        <a v-if="showNext" href="#" @click="clickNext" class="btn btn-primary">Next</a>
        <a v-if="showSubmit && rightSidebarView === 'Publish'" href="#" @click="clickSubmit" class="btn btn-primary">Submit</a>
      </div>

      <div
          v-if="publishSubmitted"
          style="background-color: #d6f0e0; color: #0d6832; padding: 20px; margin-top: -60px;"
          class="success-alert"
      >
        <i class="fas fa-check-circle me-3"></i>Responses saved successfully.
        <a href="#" class="btn-sm btn btn-outline-success" @click="publishSubmitted = false">OK</a>
      </div>

      <br />
      <br />
    </div>

    <br />
    <br />
  </div>
</template>

<script>
import { ref, nextTick } from 'vue'
import AllGoals from '@/views/WheelAnalysis/AllGoals/AllGoals.vue'
import PrimaryGoals from '@/views/WheelAnalysis/PrimaryGoals/PrimaryGoals.vue'
import GoalDetails from '@/views/WheelAnalysis/GoalDetails/GoalDetails.vue'
import StrategyDetails from '@/views/CrossWalkAnalysis/StrategyDetails/StrategyDetails.vue'
import SingleStrategySetup from '@/views/CrossWalkAnalysis/SingleStrategySetup/SingleStrategySetup.vue'
import { useGoalDetailsStore } from '@/stores/WheelAnalysis/goalDetails'
import { useAllGoalsStore } from '@/stores/WheelAnalysis/allGoals'
import { useStrategyDetailsStore } from '@/stores/CrossWalkAnalysis/strategyDetails'
import { useCrosswalkChallengeStore } from '@/stores/CrossWalkAnalysis/challenge'
import { useChallengeStore } from '@/stores/WheelAnalysis/challenge'
import { useUserInfoStore } from '@/stores/Setup/userInfo'
import ChallengeView from '@/views/CrossWalkAnalysis/ChallengeView/ChallengeView.vue'
import AboutOrg from '@/views/Setup/AboutOrg/AboutOrg.vue'
import MatrixType from '@/views/CrossWalkAnalysis/MatrixType/MatrixType.vue'
import AboutMe from '@/views/Setup/AboutMeSidebar/AboutMe.vue'
import ChallengeType from '@/views/WheelAnalysis/ChallengeType/ChallengeType.vue'
import ChallengeStatement from '@/views/WheelAnalysis/ChallengeStatement/ChallengeStatement.vue'
import KeywordView from '@/views/WheelAnalysis/KeywordView/KeywordView.vue'
import ImageView from '@/views/WheelAnalysis/ImageView/ImageView.vue'
import CrosswalkLegend from '@/views/CrossWalkAnalysis/CrosswalkLegend/CrosswalkLegend.vue'
import { storeToRefs } from 'pinia'
import WheelLegend from '@/views/WheelAnalysis/WheelLegend/WheelLegend.vue'
import ReviewGoals from '@/views/WheelAnalysis/ReviewGoals/ReviewGoals.vue'
import AnalysisName from '@/views/WheelAnalysis/AnalysisName/AnalysisName.vue'
import CrosswalkAnalysisName from '@/views/CrossWalkAnalysis/CrosswalkAnalysisName/CrosswalkAnalysisName.vue'
import DownloadData from '@/views/CrossWalkAnalysis/DownloadData/DownloadData.vue'
import PublishData from '@/views/CrossWalkAnalysis/PublishData/PublishData'

export default {
  name: 'RightSideBar',
  emits: ['print', 'print-matrix-only', 'download-csv'],
  components: {
    PublishData,
    CrosswalkAnalysisName,
    AnalysisName,
    ReviewGoals,
    WheelLegend,
    CrosswalkLegend,
    ImageView,
    AboutOrg,
    ChallengeView,
    AllGoals,
    PrimaryGoals,
    GoalDetails,
    StrategyDetails,
    SingleStrategySetup,
    MatrixType,
    AboutMe,
    ChallengeType,
    ChallengeStatement,
    KeywordView,
    DownloadData
  },
  props: {
    viewMode: {
      default: 'wheel'
    }
  },
  data() {
    return {
      goalOrTarget: 'Target',
      strategySetupMode: false,
      setupStrategyId: 0,
      primaryGoalTitle: '',
      publishSubmitted: false
    }
  },
  setup(props, { expose }) {
    const sidebarEl = ref(null)

    const focus = () => {
      sidebarEl.value?.focus()
    }
    expose({ focus })

    const strategyDetailsStore = useStrategyDetailsStore()
    const { rightSidebarView } = storeToRefs(strategyDetailsStore)

    const goalDetailsStore = useGoalDetailsStore()
    const userInfoStore = useUserInfoStore()
    const crosswalkChallengeStore = useCrosswalkChallengeStore()
    const allGoalsStore = useAllGoalsStore()
    const activeTabId1 = ref('selections')
    const wheelChallengeStore = useChallengeStore()

    const { showNext, showSubmit, showPrevious, nextPage, previousPage } = storeToRefs(strategyDetailsStore)
    const { primaryGoalsViewMode, nextPrimaryGoalsViewMode, previousPrimaryGoalsViewMode } = storeToRefs(allGoalsStore)

    return {
      // refs
      sidebarEl,
      activeTabId1,
      // stores/refs
      strategyDetailsStore,
      rightSidebarView,
      goalDetailsStore,
      showNext,
      showPrevious,
      showSubmit,
      nextPage,
      previousPage,
      primaryGoalsViewMode,
      nextPrimaryGoalsViewMode,
      previousPrimaryGoalsViewMode,
      userInfoStore,
      crosswalkChallengeStore,
      wheelChallengeStore
    }
  },
  mounted() {
    this.nextPage = 'ChallengeStatement'
    this.rightSidebarView = 'ChallengeType'
    this.nextPrimaryGoalsViewMode = 'primary'

    // If goal details change, update the label; don't touch the tab
    this.goalDetailsStore.$subscribe(
        () => {
          nextTick(() => {
            this.goalOrTarget = this.goalDetailsStore.currentDetailType
          })
        },
        { detached: true }
    )

    // Strategy details changes → default to selections tab
    this.strategyDetailsStore.$subscribe(
        () => {
          nextTick(() => {
            //this.activeTabId1 = 'selections'
            this.strategySetupMode = this.strategyDetailsStore.strategySetupMode
            this.setupStrategyId = this.strategyDetailsStore.setupStrategyId
          })
        },
        { detached: true }
    )

    this.focusSidebar()
  },
  watch: {
    rightSidebarView(newVal) {
      this.focusSidebar()

      // choose tab based on the new view
      nextTick(() => {
        this.activeTabId1 =
            (this.viewMode === 'wheel' && (newVal === 'goal' || newVal === 'target'))
                ? 'details'
                : 'selections'
      })

      if (this.rightSidebarView === 'goal' || this.rightSidebarView === 'target')
        this.showPrevious = false
    }
    },
    viewMode() {
      if (this.viewMode === 'Publish') {
        this.showNext = false
        this.showPrevious = false
        this.showSubmit = true
      } else {
        this.showSubmit = false
      }

  },
  methods: {
    focusSidebar() {
      // Only focus; do not reset the active tab here
      nextTick(() => {
        requestAnimationFrame(() => {
          this.sidebarEl?.focus()
        })
      })
    },
    clickSubmit() {
      if (this.userInfoStore.consentShareData && this.viewMode.toLowerCase() === 'wheel') {
        this.wheelChallengeStore.isPublished = true
        this.wheelChallengeStore.isPublishedChanged()
      } else if (this.userInfoStore.consentShareData) {
        this.crosswalkChallengeStore.isPublished = true
        this.crosswalkChallengeStore.isPublishedChanged()
      }
      this.publishSubmitted = true
    },
    clickNext() {
      this.showNext = true
      this.showPrevious = true
      this.rightSidebarView = this.nextPage

      if (this.viewMode.toLowerCase() === 'wheel') {

        switch (this.rightSidebarView) {
          case 'ChallengeType':
            this.nextPage = 'ChallengeStatement'
            this.previousPage = ''
            break
          case 'ChallengeStatement':
            this.nextPage = 'ImageView'
            this.previousPage = 'ChallengeType'
            this.nextPrimaryGoalsViewMode = 'primary'
            break
          case 'ImageView':
            this.nextPrimaryGoalsViewMode = 'primary'
            this.nextPage = 'PrimaryGoals'
            this.previousPage = 'ChallengeStatement'
            break
          case 'PrimaryGoals':
            this.primaryGoalsViewMode = this.nextPrimaryGoalsViewMode
            if (this.primaryGoalsViewMode === 'primary') {
              this.nextPrimaryGoalsViewMode = 'secondary'
              this.previousPrimaryGoalsViewMode = ''
              this.primaryGoalTitle = 'Primary SDG'
              this.nextPage = 'PrimaryGoals'
              this.previousPage = 'ImageView'
              scroll(0, 0)
            } else if (this.primaryGoalsViewMode === 'secondary') {
              this.nextPrimaryGoalsViewMode = 'sweep'
              this.previousPrimaryGoalsViewMode = 'primary'
              this.primaryGoalTitle = 'Secondary SDGs'
              this.nextPage = 'PrimaryGoals'
              this.previousPage = 'PrimaryGoals'
              scroll(0, 0)
            } else if (this.primaryGoalsViewMode === 'sweep') {
              this.nextPrimaryGoalsViewMode = 'review'
              this.previousPrimaryGoalsViewMode = 'secondary'
              this.primaryGoalTitle = 'Additional Targets'
              this.nextPage = 'PrimaryGoals'
              this.previousPage = 'PrimaryGoals'
              scroll(0, 0)
            } else if (this.primaryGoalsViewMode === 'review') {
              this.nextPrimaryGoalsViewMode = 'primary'
              this.previousPrimaryGoalsViewMode = 'sweep'
              this.primaryGoalTitle = 'Review SDGs'
              this.nextPage = 'KeywordView'
              this.previousPage = 'PrimaryGoals'
            }
            break
          case 'KeywordView':
            this.nextPrimaryGoalsViewMode = 'primary'
            this.nextPage = 'ReviewWheel'
            this.previousPage = 'PrimaryGoals'
            break
          case 'ReviewWheel':
            this.nextPrimaryGoalsViewMode = 'primary'
            this.nextPage = 'PrimaryGoals'
            this.previousPage = 'KeywordView'
            this.showNext = false
            break
          case 'AnalysisName':
            this.nextPrimaryGoalsViewMode = ''
            this.nextPage = ''
            this.previousPage = ''
            this.showNext = false
            break
          case 'CrosswalkAnalysisName':
            this.nextPrimaryGoalsViewMode = ''
            this.nextPage = ''
            this.previousPage = ''
            this.showNext = false
            break
        }
      }

      this.focusSidebar()
    },
    clickPrevious() {
      this.showNext = true
      this.showPrevious = true
      this.rightSidebarView = this.previousPage

      if (this.viewMode.toLowerCase() === 'wheel') {

        switch (this.rightSidebarView) {
          case 'ChallengeType':
            this.nextPage = 'ChallengeStatement'
            this.previousPage = ''
            break
          case 'ChallengeStatement':
            this.nextPage = 'ImageView'
            this.previousPage = 'ChallengeType'
            this.nextPrimaryGoalsViewMode = 'primary'
            break
          case 'ImageView':
            this.nextPrimaryGoalsViewMode = 'primary'
            this.nextPage = 'PrimaryGoals'
            this.previousPage = 'ChallengeStatement'
            break
          case 'PrimaryGoals':
            this.primaryGoalsViewMode = this.nextPrimaryGoalsViewMode
            if (this.primaryGoalsViewMode === 'primary') {
              this.nextPrimaryGoalsViewMode = 'secondary'
              this.previousPrimaryGoalsViewMode = ''
              this.primaryGoalTitle = 'Primary SDG'
              this.nextPage = 'PrimaryGoals'
              this.previousPage = 'ImageView'
              scroll(0, 0)
            } else if (this.primaryGoalsViewMode === 'secondary') {
              this.nextPrimaryGoalsViewMode = 'sweep'
              this.previousPrimaryGoalsViewMode = 'primary'
              this.primaryGoalTitle = 'Secondary SDGs'
              this.nextPage = 'PrimaryGoals'
              this.previousPage = 'PrimaryGoals'
              scroll(0, 0)
            } else if (this.primaryGoalsViewMode === 'sweep') {
              this.nextPrimaryGoalsViewMode = 'review'
              this.previousPrimaryGoalsViewMode = 'secondary'
              this.primaryGoalTitle = 'Additional Targets'
              this.nextPage = 'PrimaryGoals'
              this.previousPage = 'PrimaryGoals'
              scroll(0, 0)
            } else if (this.primaryGoalsViewMode === 'review') {
              this.nextPrimaryGoalsViewMode = 'primary'
              this.previousPrimaryGoalsViewMode = 'sweep'
              this.primaryGoalTitle = 'Review SDGs'
              this.nextPage = 'KeywordView'
              this.previousPage = 'PrimaryGoals'
            }
            break
          case 'KeywordView':
            this.nextPrimaryGoalsViewMode = 'primary'
            this.nextPage = 'ReviewWheel'
            this.previousPage = 'PrimaryGoals'
            break
          case 'ReviewWheel':
            this.nextPrimaryGoalsViewMode = 'primary'
            this.nextPage = 'PrimaryGoals'
            this.previousPage = 'KeywordView'
            this.showNext = false
            break
          case 'AnalysisName':
            this.nextPrimaryGoalsViewMode = ''
            this.nextPage = ''
            this.previousPage = ''
            this.showNext = false
            break
          case 'CrosswalkAnalysisName':
            this.nextPrimaryGoalsViewMode = ''
            this.nextPage = ''
            this.previousPage = ''
            this.showNext = false
            break
        }
      }

      this.focusSidebar()
    }
  }
}
</script>
